import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { CartProvider } from './contexts/CartContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { VoiceProvider, useVoice } from './contexts/voice';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import VoiceOverlay from './components/VoiceOverlay';
import TutorialOverlay from './components/TutorialOverlay';
import PhoneNumberModal from './components/PhoneNumberModal';
import ProductModal from './components/ProductModal';
import TrackingCard from './components/TrackingCard';
import PickupConfirmation from './components/PickupConfirmation';
import CallbackConfirmation from './components/CallbackConfirmation';
import HandoverOverlay from './components/HandoverOverlay';
import StorePage from './pages/StorePage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import { AdminPage } from './pages/admin';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SplashPage from './pages/SplashPage';
import RequestAccessPage from './pages/RequestAccessPage';
import { Loader2 } from 'lucide-react';

function PageWithHeader({ children, showSearch = true }: { children: React.ReactNode; showSearch?: boolean }) {
  return (
    <>
      <Header />
      {showSearch && <SearchBar />}
      <div className="animate-enter">
        {children}
      </div>
    </>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center animate-enter">
        <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Laden...</p>
      </div>
    </div>
  );
}

const PHONE_DISMISS_KEY = 'phone_modal_dismissed';

function AppContent() {
  const [voiceExpanded, setVoiceExpanded] = useState(false);
  const { getAuthHeaders } = useUser();
  const { hasCompletedTutorial, startTutorial, isActive: tutorialActive } = useTutorial();
  const { trackingData, dismissTracking, pickupConfirmation, dismissPickupConfirmation, callbackConfirmation, dismissCallbackConfirmation, handoverData, dismissHandover, selectedProduct, setSelectedProduct } = useVoice();
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Check if user has a phone number on mount
  useEffect(() => {
    const dismissed = sessionStorage.getItem(PHONE_DISMISS_KEY);
    if (dismissed) return;

    const checkPhone = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/user/profile', { headers });
        const data = await res.json();
        if (!data.phone) {
          setShowPhoneModal(true);
        }
      } catch {
        // Silently ignore - don't block the app
      }
    };
    checkPhone();
  }, [getAuthHeaders]);

  // Auto-start tutorial for first-time users (after a short delay)
  useEffect(() => {
    if (!hasCompletedTutorial && !tutorialActive) {
      const timer = setTimeout(() => {
        startTutorial();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasCompletedTutorial, tutorialActive, startTutorial]);

  return (
    <>
      <Routes>
        <Route path="/" element={<PageWithHeader><StorePage /></PageWithHeader>} />
        <Route path="/winkelwagen" element={<PageWithHeader showSearch={false}><CartPage /></PageWithHeader>} />
        <Route path="/afrekenen" element={<PageWithHeader showSearch={false}><CheckoutPage /></PageWithHeader>} />
        <Route path="/bevestiging/:orderId" element={<PageWithHeader showSearch={false}><OrderConfirmationPage /></PageWithHeader>} />
        <Route path="/bestellingen" element={<PageWithHeader showSearch={false}><OrderHistoryPage /></PageWithHeader>} />
        <Route path="/admin" element={<PageWithHeader showSearch={false}><AdminPage /></PageWithHeader>} />
      </Routes>

      {/* Voice chat bubble - available on all pages */}
      <VoiceOverlay
        expanded={voiceExpanded}
        onToggleExpand={() => setVoiceExpanded(!voiceExpanded)}
      />

      {/* Tutorial overlay */}
      <TutorialOverlay />

      {/* Tracking card - shown when agent provides tracking info */}
      {trackingData && (
        <TrackingCard
          orderId={trackingData.orderId}
          tracking={trackingData.tracking}
          items={trackingData.items}
          onDismiss={dismissTracking}
        />
      )}

      {/* Pickup confirmation card - shown when furniture pickup is scheduled */}
      {pickupConfirmation && (
        <PickupConfirmation
          data={pickupConfirmation}
          onDismiss={dismissPickupConfirmation}
        />
      )}

      {/* Callback confirmation - shown when callback is scheduled */}
      {callbackConfirmation && (
        <CallbackConfirmation
          confirmationNumber={callbackConfirmation.confirmationNumber}
          day={callbackConfirmation.day}
          date={callbackConfirmation.date}
          time={callbackConfirmation.time}
          reason={callbackConfirmation.reason}
          onDismiss={dismissCallbackConfirmation}
        />
      )}

      {/* Handover overlay - shown during agent transfer */}
      {handoverData && (
        <HandoverOverlay
          status={handoverData.status}
          summary={handoverData.summary}
          newAgent={handoverData.newAgent}
          onDismiss={dismissHandover}
        />
      )}

      {/* Product detail modal - rendered at app root to avoid transform containment */}
      <ProductModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />

      {/* Phone number prompt - shown once per session if no phone registered */}
      <PhoneNumberModal
        isOpen={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false);
          sessionStorage.setItem(PHONE_DISMISS_KEY, '1');
        }}
        onSaved={() => {
          setShowPhoneModal(false);
          sessionStorage.setItem(PHONE_DISMISS_KEY, '1');
        }}
      />
    </>
  );
}

function AuthenticatedApp() {
  const { user, loading } = useUser();

  // Show loading screen while checking auth state
  if (loading) {
    return <LoadingScreen />;
  }

  // Show splash page if not authenticated
  if (!user) {
    return <SplashPage />;
  }

  // User is authenticated - show the app with voice support
  // TutorialProvider must be inside CartProvider and VoiceProvider as it uses both
  return (
    <CartProvider>
      <VoiceProvider>
        <TutorialProvider>
          <AppContent />
        </TutorialProvider>
      </VoiceProvider>
    </CartProvider>
  );
}

export default function App() {
  return (
    <UserProvider>
      <LanguageProvider>
        <ThemeProvider>
          <Routes>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="*" element={<AuthenticatedApp />} />
          </Routes>
        </ThemeProvider>
      </LanguageProvider>
    </UserProvider>
  );
}
